import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApimHandler } from '@api/apim-handler';
import { buildFallbackGroupsFromEndpoints } from './utils';
import { ApiEndpoint, ApiGroup, CatalogStats } from './types';

/*
 * Loads and manages the API Portal overview: catalog stats, groups (falling back to
 * endpoint-derived groups when the groups API returns none populated), and all endpoints.
 */
export function useApiPortal() {
    const [apiGroups, setApiGroups] = useState<ApiGroup[]>([]);
    const [allEndpoints, setAllEndpoints] = useState<ApiEndpoint[]>([]);
    const [stats, setStats] = useState<CatalogStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const fetchOverview = useCallback(async () => {
        setIsLoading(true);
        setHasError(false);
        try {
            const [groupsRes, statsRes, endpointsRes] = await Promise.all([
                ApimHandler.fetchApiGroups(),
                ApimHandler.fetchCatalogStats(),
                ApimHandler.fetchAllEndpoints(),
            ]);
            setApiGroups(groupsRes.data);
            setStats(statsRes.data);
            setAllEndpoints(endpointsRes.data);
        } catch {
            setHasError(true);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOverview();
    }, [fetchOverview]);

    const displayGroups = useMemo(() => {
        const hasValidGroups = apiGroups.length > 0 && apiGroups.some((g) => g.endpoints && g.endpoints.length > 0);
        return hasValidGroups ? apiGroups : buildFallbackGroupsFromEndpoints(allEndpoints);
    }, [apiGroups, allEndpoints]);

    return { stats, allEndpoints, isLoading, hasError, displayGroups, retry: fetchOverview };
}
